import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Field } from "react-final-form";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SmartDisplayOutlinedIcon from "@mui/icons-material/SmartDisplayOutlined";

import styles from "./ConsentBundelPlaylistModel.module.scss";

const API_KEY = "AIzaSyCKfUSr5PacGOhWURYgZlgjmgGh4Oc5kjE";

interface ConsentBundelPlaylistModelProps {
  open: boolean;
  onClose: () => void;
  listId: number | null;
  consentBundleId: number | null;
  onSuccess: (data?: any) => void;
  onUploadStart?: () => void;
  onError?: (message?: string) => void;
}

type VideoPlatform = "youtube" | "tiktok" | null;

type VideoDetails = {
  title: string;
  description: string;
  thumbnail: string;
  duration: string;
};

type YouTubeSearchResult = {
  id?: {
    videoId?: string;
  };
  snippet?: {
    title?: string;
    description?: string;
    thumbnails?: {
      default?: {
        url?: string;
      };
      medium?: {
        url?: string;
      };
      high?: {
        url?: string;
      };
    };
  };
};

const emptyVideoDetails: VideoDetails = {
  title: "",
  description: "",
  thumbnail: "",
  duration: "",
};

const detectVideoPlatform = (url: string): VideoPlatform => {
  if (!url) return null;

  const youtubeRegex =
    /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/i;

  const tiktokRegex =
    /^(https?:\/\/)?(www\.)?(tiktok\.com\/(@[\w.-]+\/video\/\d+|t\/\w+))/i;

  if (youtubeRegex.test(url)) return "youtube";
  if (tiktokRegex.test(url)) return "tiktok";

  return null;
};

const extractYouTubeVideoId = (url: string) => {
  const regex =
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/i;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const buildYouTubeUrl = (videoId: string) =>
  `https://www.youtube.com/watch?v=${videoId}`;

const buildYouTubeThumb = (videoId: string) =>
  `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

const formatISODuration = (isoDuration: string): string => {
  const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
  const matches = isoDuration.match(regex);

  if (!matches) return "0:00";

  const hours = parseInt(matches[1] || "0", 10);
  const minutes = parseInt(matches[2] || "0", 10);
  const seconds = parseInt(matches[3] || "0", 10);

  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
        2,
        "0"
      )}`
    : `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const getVideoDetails = async (videoId: string): Promise<VideoDetails | null> => {
  const ytUrl = `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoId}&key=${API_KEY}`;

  try {
    const response = await fetch(ytUrl);
    const data = await response.json();

    if (data?.error?.message) {
      throw new Error(data.error.message);
    }

    if (data.items?.length > 0) {
      const videoData = data.items[0];
      const title = videoData.snippet?.title || "";
      const description = videoData.snippet?.description || "";
      const thumbnail =
        videoData.snippet?.thumbnails?.high?.url ||
        videoData.snippet?.thumbnails?.medium?.url ||
        buildYouTubeThumb(videoId);
      const rawDuration = videoData.contentDetails?.duration || "PT0S";
      const duration = formatISODuration(rawDuration);

      return { title, description, thumbnail, duration };
    }

    throw new Error("Video not found");
  } catch (error) {
    console.error("Error fetching video details:", error);
    return null;
  }
};

const searchYouTubeVideos = async (
  query: string,
  pageToken: string | null = null
): Promise<{
  items: YouTubeSearchResult[];
  nextPageToken: string | null;
  error?: string;
}> => {
  const baseUrl = `https://www.googleapis.com/youtube/v3/search?part=snippet&type=video&maxResults=20&q=${encodeURIComponent(
    query
  )}&key=${API_KEY}`;

  const requestUrl = pageToken ? `${baseUrl}&pageToken=${pageToken}` : baseUrl;

  try {
    const response = await fetch(requestUrl);
    const data = await response.json();

    if (data?.error?.message) {
      throw new Error(data.error.message);
    }

    return {
      items: data.items || [],
      nextPageToken: data.nextPageToken || null,
    };
  } catch (error: any) {
    console.error("YouTube Search Error:", error);

    return {
      items: [],
      nextPageToken: null,
      error: error?.message || "Failed to search YouTube videos.",
    };
  }
};

const ConsentBundelPlaylistModel: React.FC<ConsentBundelPlaylistModelProps> = ({
  open,
  onClose,
  listId,
  consentBundleId,
  onSuccess,
  onUploadStart,
  onError,
}) => {
  const [videoDetails, setVideoDetails] =
    useState<VideoDetails>(emptyVideoDetails);
  const [url, setUrl] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<YouTubeSearchResult[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewVideoId, setPreviewVideoId] = useState<string | null>(null);
  const [localError, setLocalError] = useState("");

  const selectedVideoId = useMemo(() => extractYouTubeVideoId(url), [url]);

  const resetVideoDetails = (form?: any) => {
    form?.change("title", "");
    form?.change("description", "");

    setVideoDetails(emptyVideoDetails);
  };

  const resetLocalState = (form?: any) => {
    setUrl("");
    setSearchQuery("");
    setSearchResults([]);
    setNextPageToken(null);
    setShowResults(false);
    setIsSearching(false);
    setIsSaving(false);
    setPreviewOpen(false);
    setPreviewVideoId(null);
    setLocalError("");
    resetVideoDetails(form);
  };

  useEffect(() => {
    if (!open) {
      resetLocalState();
    }
  }, [open]);

  const applyYouTubeVideo = async (videoId: string, form: any) => {
    const fullUrl = buildYouTubeUrl(videoId);

    setUrl(fullUrl);
    setSearchQuery(fullUrl);
    setLocalError("");

    const details = await getVideoDetails(videoId);

    if (!details) {
      const fallbackDetails = {
        title: "",
        description: "",
        thumbnail: buildYouTubeThumb(videoId),
        duration: "",
      };

      setVideoDetails(fallbackDetails);
      form.change("url", fullUrl);
      form.change("title", "");
      form.change("description", "");
      onError?.("Failed to fetch video details.");
      return;
    }

    setVideoDetails(details);
    form.change("url", fullUrl);
    form.change("title", details.title);
    form.change("description", details.description);
    setShowResults(false);
  };

  const handleScrubVideoDetails = async (nextUrl: string, form: any) => {
    const platform = detectVideoPlatform(nextUrl);

    if (!platform) {
      resetVideoDetails(form);
      return;
    }

    if (platform === "youtube") {
      const videoId = extractYouTubeVideoId(nextUrl);

      if (!videoId) {
        resetVideoDetails(form);
        return;
      }

      await applyYouTubeVideo(videoId, form);
      return;
    }

    if (platform === "tiktok") {
      const details = {
        title: "TikTok Video",
        description: "",
        thumbnail: "",
        duration: "",
      };

      setVideoDetails(details);
      form.change("title", details.title);
      form.change("description", details.description);
    }
  };

  const handleSearch = async (form: any) => {
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      setLocalError("Enter a YouTube search keyword or paste a YouTube link.");
      return;
    }

    const pastedVideoId = extractYouTubeVideoId(trimmedQuery);

    if (pastedVideoId) {
      await applyYouTubeVideo(pastedVideoId, form);
      return;
    }

    setLocalError("");
    setIsSearching(true);

    const { items, nextPageToken: token, error } = await searchYouTubeVideos(
      trimmedQuery
    );

    if (error) {
      onError?.(error);
    }

    setSearchResults(items);
    setNextPageToken(token);
    setShowResults(true);
    setIsSearching(false);
  };

  const handleLoadMore = async () => {
    if (!nextPageToken || isSearching) return;

    setIsSearching(true);

    const {
      items,
      nextPageToken: token,
      error,
    } = await searchYouTubeVideos(searchQuery.trim(), nextPageToken);

    if (error) {
      onError?.(error);
    }

    setSearchResults((prev) => [...prev, ...items]);
    setNextPageToken(token);
    setIsSearching(false);
  };

  const validate = (values: any) => {
    const errors: any = {};

    if (!values.url) {
      errors.url = "Video URL is required";
      return errors;
    }

    const platform = detectVideoPlatform(values.url);

    if (!platform) {
      errors.url = "Only YouTube or TikTok video links are allowed";
    }

    if (!values.title && !videoDetails.title) {
      errors.title = "Video title is required";
    }

    return errors;
  };

  const submitDataYt = async (values: any) => {
    const submitUrl = values.url || url;
    const platform = detectVideoPlatform(submitUrl);

    if (!platform) {
      onError?.("Only YouTube or TikTok links are allowed");
      return false;
    }

    if (!consentBundleId) {
      onError?.("Consent bundle ID is missing.");
      return false;
    }

    const payload = {
      title: values.title || videoDetails.title,
      description: values.description || videoDetails.description,
      url: submitUrl,
      duration: videoDetails.duration,
      thumbnail: videoDetails.thumbnail,
      platform,
    };

    try {
      setIsSaving(true);
      onUploadStart?.();

      const response = await axios.put(
        `consent_bundle/${consentBundleId}/add_video`,
        payload
      );

      if (response.status === 200) {
        onSuccess?.(response.data);
        onClose();
        return true;
      }

      onError?.("Failed to add video.");
      return false;
    } catch (error: any) {
      console.error("Error submitting data:", error);

      const rawMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "";

      const normalizedMessage = Array.isArray(rawMessage)
        ? rawMessage.join(", ")
        : String(rawMessage);

      if (normalizedMessage.toLowerCase().includes("duplicate")) {
        onError?.("This video is already added.");
      } else {
        onError?.(normalizedMessage || "Failed to add video.");
      }

      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      PaperProps={{
        className: styles.modalPaper,
      }}
    >
      <div className={styles.modalHeader}>
        <div className={styles.modalTitleWrap}>
          <div className={styles.titleIcon}>
            <SmartDisplayOutlinedIcon fontSize="small" />
          </div>

          <div>
            <h2 className={styles.modalTitle}>Add YouTube Video</h2>
            <p className={styles.modalSubtitle}>
              Search YouTube, select a result, or paste a YouTube/TikTok link.
            </p>
          </div>
        </div>

        <IconButton
          className={styles.closeButton}
          onClick={() => {
            resetLocalState();
            onClose();
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent className={styles.modalContent}>
        <Form
          onSubmit={(values) => submitDataYt(values)}
          validate={validate}
          initialValues={{
            url: "",
            title: "",
            description: "",
          }}
          render={({ form, values }) => (
            <form
              className={styles.form}
              onSubmit={async (event) => {
                event.preventDefault();
                const success = await submitDataYt(values);

                if (success) {
                  form.reset();
                  resetLocalState(form);
                }
              }}
            >
              <div className={styles.fieldCard}>
                <div className={styles.searchRow}>
                  <TextField
                    className={styles.searchInput}
                    label="Search YouTube videos"
                    fullWidth
                    size="small"
                    value={searchQuery}
                    onChange={(event) => {
                      setSearchQuery(event.target.value);
                      setLocalError("");
                    }}
                    onPaste={(event) => {
                      const pastedText = event.clipboardData
                        .getData("text")
                        .trim();

                      if (!pastedText) return;

                      setTimeout(async () => {
                        setSearchQuery(pastedText);

                        const pastedVideoId = extractYouTubeVideoId(pastedText);

                        if (pastedVideoId) {
                          await applyYouTubeVideo(pastedVideoId, form);
                        }
                      }, 0);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleSearch(form);
                      }
                    }}
                    error={Boolean(localError)}
                    helperText={localError}
                  />

                  <Button
                    className={styles.secondaryActionButton}
                    variant="outlined"
                    onClick={() => handleSearch(form)}
                    disabled={isSearching}
                  >
                    {isSearching ? "Searching..." : "Search"}
                  </Button>
                </div>

                {showResults && (
                  <div className={styles.resultsViewport}>
                    {searchResults.length > 0 ? (
                      <div className={styles.resultsGrid}>
                        {searchResults.map((video) => {
                          const videoId = video.id?.videoId;

                          if (!videoId) return null;

                          const title =
                            video.snippet?.title || "Untitled YouTube video";
                          const thumbnail =
                            video.snippet?.thumbnails?.medium?.url ||
                            video.snippet?.thumbnails?.high?.url ||
                            buildYouTubeThumb(videoId);

                          return (
                            <Card
                              key={videoId}
                              className={styles.videoCard}
                              onClick={() => applyYouTubeVideo(videoId, form)}
                            >
                              <IconButton
                                aria-label="Preview video"
                                className={styles.previewButton}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setPreviewVideoId(videoId);
                                  setPreviewOpen(true);
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>

                              {selectedVideoId === videoId && (
                                <span className={styles.selectedResultBadge}>
                                  Selected
                                </span>
                              )}

                              <CardMedia
                                component="img"
                                image={thumbnail}
                                alt={title}
                                className={styles.videoThumb}
                              />

                              <CardContent sx={{ p: 1.2 }}>
                                <Typography className={styles.videoTitle}>
                                  {title.length > 74
                                    ? `${title.slice(0, 71)}...`
                                    : title}
                                </Typography>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    ) : (
                      <div className={styles.emptyState}>
                        No YouTube videos found.
                      </div>
                    )}

                    {nextPageToken && (
                      <Box className={styles.loadMoreWrap}>
                        <Button
                          className={styles.secondaryActionButton}
                          variant="outlined"
                          onClick={handleLoadMore}
                          disabled={isSearching}
                        >
                          {isSearching ? "Loading..." : "Load More"}
                        </Button>
                      </Box>
                    )}
                  </div>
                )}

                <Field name="url">
                  {({ input, meta }) => (
                    <TextField
                      {...input}
                      label="YouTube or TikTok video URL"
                      fullWidth
                      required
                      size="small"
                      value={url}
                      onChange={async (event) => {
                        const nextUrl = event.target.value;

                        setUrl(nextUrl);
                        input.onChange(nextUrl);
                        await handleScrubVideoDetails(nextUrl, form);
                      }}
                      onPaste={(event) => {
                        const pastedText = event.clipboardData
                          .getData("text")
                          .trim();

                        if (!pastedText) return;

                        setTimeout(async () => {
                          setUrl(pastedText);
                          input.onChange(pastedText);
                          await handleScrubVideoDetails(pastedText, form);
                        }, 0);
                      }}
                      onBlur={() => {
                        if (url.trim()) {
                          handleScrubVideoDetails(url.trim(), form);
                        }
                      }}
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>

                {videoDetails.thumbnail && (
                  <div className={styles.selectedPreview}>
                    <img
                      src={videoDetails.thumbnail}
                      alt="Video Thumbnail"
                      className={styles.thumbnailPreview}
                    />

                    <div className={styles.selectedPreviewContent}>
                      <span className={styles.selectedLabel}>
                        Selected video
                      </span>
                      <strong>{videoDetails.title}</strong>
                      {videoDetails.duration && <small>{videoDetails.duration}</small>}
                    </div>
                  </div>
                )}

                {(url || videoDetails.title) && (
                  <div className={styles.detailsGrid}>
                    <Field name="title">
                      {({ input, meta }) => (
                        <TextField
                          {...input}
                          label="Video Title"
                          fullWidth
                          required
                          size="small"
                          value={input.value || videoDetails.title}
                          onChange={(event) => {
                            input.onChange(event.target.value);
                            setVideoDetails((prev) => ({
                              ...prev,
                              title: event.target.value,
                            }));
                          }}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                        />
                      )}
                    </Field>

                    <Field name="description">
                      {({ input, meta }) => (
                        <TextField
                          {...input}
                          label="Description"
                          fullWidth
                          multiline
                          rows={3}
                          size="small"
                          value={input.value || videoDetails.description}
                          onChange={(event) => {
                            input.onChange(event.target.value);
                            setVideoDetails((prev) => ({
                              ...prev,
                              description: event.target.value,
                            }));
                          }}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                        />
                      )}
                    </Field>
                  </div>
                )}
              </div>

              <DialogActions className={styles.actions}>
                <Button
                  className={styles.cancelButton}
                  onClick={() => {
                    form.reset();
                    resetLocalState(form);
                    onClose();
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>

                <Button
                  className={styles.primaryButton}
                  type="submit"
                  variant="contained"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <CircularProgress size={16} thickness={5} />
                      Adding...
                    </>
                  ) : (
                    "Add"
                  )}
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </DialogContent>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        PaperProps={{
          className: styles.previewDialogPaper,
        }}
      >
        <div className={styles.previewModalBox}>
          {previewVideoId && (
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${previewVideoId}?autoplay=1&mute=0&controls=1`}
              title="YouTube video player"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          )}
        </div>
      </Dialog>
    </Dialog>
  );
};

export default ConsentBundelPlaylistModel;
